import { useEffect, useLayoutEffect, useRef, type KeyboardEvent, type RefObject } from 'react'

const TABBABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
  '[contenteditable="true"]',
].join(',')

/** The elements Tab can reach inside `root`, in DOM order. */
export function tabbables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(TABBABLE)].filter(
    (el) =>
      el.tabIndex >= 0 && !el.hidden && !el.closest('[hidden], [inert]') && el.getAttribute('aria-hidden') !== 'true',
  )
}

export interface UseDialogOptions {
  onClose: () => void
  /** false: Escape and the backdrop don't close it (C-13b, the leave prompt). */
  dismissible: boolean
  /** Where focus goes first. Default: the first tabbable element, else the dialog itself. */
  initialFocus?: RefObject<HTMLElement | null>
}

/**
 * Focus handling for Modal and Drawer (Frontend §2.4): initial focus, a Tab trap, Escape to close, return focus
 * to the trigger on close, and no page scroll behind the overlay.
 */
export function useDialog({ onClose, dismissible, initialFocus }: UseDialogOptions) {
  const dialogRef = useRef<HTMLElement>(null)
  const onCloseRef = useRef(onClose)
  useLayoutEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const dialog = dialogRef.current
    if (dialog) {
      const target = initialFocus?.current ?? tabbables(dialog)[0] ?? dialog
      target.focus()
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
      if (trigger?.isConnected) trigger.focus()
    }
    // Runs once per open: the initial focus target is read on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onKeyDown = (e: KeyboardEvent) => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (e.key === 'Escape') {
      e.stopPropagation()
      if (dismissible) onCloseRef.current()
      return
    }
    if (e.key !== 'Tab') return
    const items = tabbables(dialog)
    const first = items[0]
    const last = items[items.length - 1]
    if (!first || !last) {
      e.preventDefault()
      dialog.focus()
      return
    }
    const active = document.activeElement
    if (e.shiftKey && (active === first || active === dialog || !dialog.contains(active))) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
      e.preventDefault()
      first.focus()
    }
  }

  /** Backdrop: a press that starts on the backdrop itself (not a drag out of the dialog). */
  const onBackdropMouseDown = (e: { target: EventTarget; currentTarget: EventTarget }) => {
    if (dismissible && e.target === e.currentTarget) onCloseRef.current()
  }

  return { dialogRef, onKeyDown, onBackdropMouseDown }
}
